/**
 * Where a scanned statement line lands in the plan, DECLARED (s217, Lane 3 —
 * `PADS-PICKER-SCAN-LINK-1` / `STATEMENT-SCANNER-PLANNER-1`).
 *
 * ## What this replaces, and why it is not a refactor
 *
 * `bulk-add` used to resolve its four target fields by DISPLAY-NAME SUBSTRING, with
 * `sectionFields[0]` as a final fallback for the payee:
 *
 *     const payeeField  = findField("payee") ?? findField("company") ?? findField("name") ?? sectionFields[0];
 *     const amountField = findField("amount") ?? findField("cost");
 *
 * Measured against the live PADS section (section-inventory.md), that shape was already
 * failing silently in production-shaped data:
 *
 *   - `payee` exists in the section but is `isActive=false`, so `findField("payee")` misses
 *     and the payee landed on `service_name` only because its display name "Service name"
 *     happens to contain the substring "name". A rename to "Service" — a copy edit, not a
 *     code change — would have dropped it onto `sectionFields[0]` instead, silently.
 *   - The amount field is displayed as **"Roughly how much?"**, which contains neither
 *     "amount" nor "cost". `amountField` was therefore `undefined` on every run, and
 *     🔴 **every scanned amount was silently discarded.** Nothing errored; the row just
 *     arrived with no money in it.
 *
 * That is the DECLARATIVE-SURFACES-1 failure mode exactly: the read does not fail, it
 * returns nothing, and no one finds out. A display name is copy. A `fieldKey` is a
 * contract. This table names the contract.
 *
 * ## The rule at the call site
 *
 * A declared key that does not resolve to an ACTIVE field in the target section is a **500
 * with the unresolved key named** — never a fallback, never a guess. A wrong declaration
 * should be loud and fixable, not invisible and wrong.
 */
import { SECTION_SLUGS } from "./constants/sections";
import { FIELD_KEYS } from "./constants/fields";

/** The four things a scanned transaction knows, mapped to the declared keys that hold them. */
export interface ScannerTargetFieldKeys {
  /** The payee. Also the reconciliation key — see `normalizePayee`. */
  payee: string;
  amount: string;
  frequency: string;
  /** The raw statement descriptor, which is context rather than a value the planner typed. */
  description: string;
}

export const SCANNER_TARGET_FIELD_KEYS: Readonly<Record<string, ScannerTargetFieldKeys>> = {
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_PRE_AUTHORIZED_DEBITS]: {
    payee: FIELD_KEYS.SERVICE_NAME,
    amount: FIELD_KEYS.AMOUNT,
    frequency: FIELD_KEYS.FREQUENCY,
    description: FIELD_KEYS.NOTES,
  },
};

/**
 * The scanner's frequency vocabulary → the PADS `frequency` select's declared choices.
 *
 * These two vocabularies were never the same and the old code wrote the scanner's value
 * straight through, so a `select` field came to hold `"monthly"` when its only legal
 * choices are `Weekly · Bi-weekly · Monthly · Quarterly · Annually`. An out-of-vocabulary
 * value in a select renders as an empty control.
 *
 * `one-time` maps to NOTHING on purpose: it is not a recurrence, the select has no choice
 * for it, and inventing one here would be a template-spec decision taken in the wrong file.
 * An unmapped frequency is skipped — the row is still created, just without a frequency.
 */
export const SCANNER_FREQUENCY_TO_PADS: Readonly<Record<string, string>> = {
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annually",
};

/**
 * The row-match key. 🔴 G7 — this is the SAME normalization the estate summary already used
 * to decide "is this scanned payee already an expense I know about"
 * (`estate-summary.ts`, the only row-level matcher in the repo). It is shared rather than
 * re-typed so the two surfaces cannot drift into disagreeing about whether two payees are
 * the same payee.
 *
 * Exact equality after normalization, and nothing cleverer. A fuzzy payee matcher was sized
 * MEDIUM by Lane B2 and is deliberately NOT built here — a false merge silently welds two
 * real obligations into one row, which is worse than a duplicate the planner can see.
 */
export function normalizePayee(raw: string | null | undefined): string {
  return (raw ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * "Are these two spellings the same payee?" — PAYEE-RENAME-MERGE-1 (D-219.12, shape (b)).
 *
 * 🔴 This is built ON `normalizePayee`, never beside a re-typed copy of it. `normalizePayee`
 * is unchanged and remains the exact key; this adds the one loosening that was ruled.
 *
 * ## Why it exists
 *
 * The model renames a payee between scans of the SAME statement — measured on staging
 * (s219 Lane D, Check 6): "TD Mortgage Payment" one run, "TD Mortgage" the next. Under
 * exact equality that minted a second PADS row for one obligation, and the planner had to
 * clean up after us.
 *
 * ## The rule
 *
 * Two payees are the same when, after `normalizePayee`, they are EQUAL (the rule that
 * already shipped), or — after dropping trailing generic words — one token list is a
 * LEADING PREFIX of the other AND the shorter list has at least two tokens.
 *
 * The two-token floor is what keeps "TD" from swallowing "TD Mortgage" and "Rogers" from
 * swallowing "Rogers Bank Mastercard". A prefix must be leading: "One Hydro" is not
 * "Hydro One".
 *
 * 🔴 NEVER GUESS. This function answers about a PAIR. The caller must apply the other half
 * of the rule: a payee that matches MORE THAN ONE existing row merges with none of them by
 * fuzz — exact match only, else a new row. A false merge welds two real obligations into
 * one row, which is worse than a duplicate the planner can see and delete.
 *
 * 🔴 NOT a renamer. A match never changes the stored payee of the row it matched.
 *
 * 🔴 `server/estate-summary.ts` deliberately does NOT call this (s220 Lane E addendum 1,
 * ruling 1). Its `normalizePayee` use is a set-membership test deciding whether a scanned
 * transaction is already a known expense; a looser rule there changes a dollar figure a
 * user sees, and nothing has measured that. It keeps exact equality until it is ruled.
 *
 * Enforced by `server/__tests__/payee-match.test.ts`.
 */
const GENERIC_PAYEE_TOKENS: ReadonlySet<string> = new Set([
  "payment", "payments", "pmt", "pymt", "pay",
  "preauthorized", "preauth", "pre", "authorized",
  "pad", "bill", "autopay", "withdrawal",
]);

function payeeTokens(raw: string | null | undefined): string[] {
  const tokens = (raw ?? "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  // Drop generic words from the END only: "TD Mortgage Payment" is "TD Mortgage", but
  // "Payment Processing Inc" keeps its first token.
  let end = tokens.length;
  while (end > 0 && GENERIC_PAYEE_TOKENS.has(tokens[end - 1])) end--;
  return tokens.slice(0, end);
}

export function isSamePayee(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = normalizePayee(a);
  const nb = normalizePayee(b);
  if (na === "" || nb === "") return false;
  if (na === nb) return true;

  const ta = payeeTokens(a);
  const tb = payeeTokens(b);
  if (ta.length === 0 || tb.length === 0) return false;

  const [shorter, longer] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  if (shorter.length < 2) return false;
  return shorter.every((token, i) => token === longer[i]);
}
